import { createSelector } from 'reselect';
import { PaymentMethod } from '@wix/cashier-common/dist/src/enums/payments/PaymentMethod';
import { isWixPay } from '../utils/providers';
import { type StoreState } from '../types/StoreState';
import { type PayPalPaymentMethod } from '../types/PaymentMethods';
import { type PayPalButtonColor } from '../types/paypal';
import { Experiment } from '../types/Experiment';
import { selectPayPalMethodsEligibility, selectExperiments } from './appState';
import { selectTopProps } from './topProps';

const selectLoadPayPalScriptState = (state: StoreState) => state.requestsState.loadPayPalScript;

export const selectPayPalDto = (state: StoreState): PayPalPaymentMethod => {
  const pp = state.domain.paymentMethods.find((pm) => !!pm[PaymentMethod.PayPal]);
  return pp as PayPalPaymentMethod;
};

export const selectIsPayPalPmAvailable = createSelector(selectPayPalDto, (pm): boolean => {
  return !!pm;
});

export const selectPayPalClientId = createSelector(
  selectPayPalDto,
  (pp: PayPalPaymentMethod) => pp && pp[PaymentMethod.PayPal].clientId,
);

export const selectPayPalCreditEnabled = createSelector(
  selectPayPalDto,
  (pp: PayPalPaymentMethod) => pp && pp[PaymentMethod.PayPal].creditEnabled,
);

export const selectPayPalMerchantId = createSelector(
  selectPayPalDto,
  (pp: PayPalPaymentMethod) => pp && pp[PaymentMethod.PayPal].merchantId,
);

export const selectPayPalPaymentProviderId = createSelector(
  selectPayPalDto,
  (pp: PayPalPaymentMethod) => pp && pp.paymentProviderId,
);

export const selectPayPalBnCode = createSelector(
  selectPayPalDto,
  (pp: PayPalPaymentMethod) => pp && pp[PaymentMethod.PayPal].bnCode,
);

export const selectPayPalPmAndScriptAvailable = createSelector(
  selectIsPayPalPmAvailable,
  selectLoadPayPalScriptState,
  (isPmAvailable, scriptState) => {
    return isPmAvailable && scriptState === 'fulfilled';
  },
);

export const selectIsPayPalButtonAvailable = (state: StoreState) => {
  const isPayPalAvailable = selectPayPalPmAndScriptAvailable(state);
  const eligibility = selectPayPalMethodsEligibility(state);
  if (!state.topProps.allowPaymentMethods.includes(PaymentMethod.PayPal)) {
    return false;
  }
  if (isPayPalAvailable && eligibility[window.paypal.FUNDING.PAYPAL]) {
    return true;
  }
  return false;
};

export const selectIsCreditBtnAvailable = createSelector(
  selectPayPalPmAndScriptAvailable,
  selectPayPalMethodsEligibility,
  selectPayPalCreditEnabled,
  selectPayPalPaymentProviderId,
  selectExperiments,
  selectTopProps,
  (isPayPalAvailable, eligibility, isCreditEnabled, providerId, experiments, topProps) => {
    if (!topProps.allowPaymentMethods.includes(PaymentMethod.PayPalCredit)) {
      return false;
    }

    if (isPayPalAvailable && isCreditEnabled && eligibility[window.paypal.FUNDING.CREDIT]) {
      return isWixPay(providerId) || experiments[Experiment.EnablePPCreditFor3rdPartyProviders] === 'true';
    }

    return false;
  },
);

export const selectIsVenmoBtnAvailable = createSelector(
  selectPayPalPmAndScriptAvailable,
  selectPayPalMethodsEligibility,
  selectTopProps,
  (isPayPalAvailable, eligibility, topProps) => {
    if (!topProps.allowPaymentMethods.includes(PaymentMethod.Venmo)) {
      return false;
    }

    if (!isPayPalAvailable) {
      return false;
    }

    return eligibility[window.paypal.FUNDING.VENMO];
  },
);

export const selectIsPayLaterBtnAvailable = createSelector(
  selectPayPalPmAndScriptAvailable,
  selectPayPalMethodsEligibility,
  selectTopProps,
  (isPayPalAvailable, eligibility, topProps) => {
    if (!topProps.allowPaymentMethods.includes(PaymentMethod.PayPalPayLater)) {
      return false;
    }
    if (isPayPalAvailable && eligibility[window.paypal.FUNDING.PAYLATER]) {
      return true;
    }
    return false;
  },
);

export const selectPayPalBtnColor = createSelector(selectTopProps, (topProps): PayPalButtonColor => {
  // Always "gold"
  // https://wix.atlassian.net/browse/PAY-10589
  return topProps.theme === 'light' ? 'gold' : 'gold';
});
